import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(onComplete, 500);
          return 100;
        }
        return prev + 1;
      });
    }, 50);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="splash-screen"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 50%, #F3F4F6 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: '#1F2937',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(28, 86, 61, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(28, 86, 61, 0.05) 0%, transparent 50%)',
        zIndex: 1
      }} />

      {/* Main Content */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
        style={{ 
          textAlign: 'center', 
          zIndex: 2,
          position: 'relative'
        }}
      >
        {/* OffCourt Logo */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
          style={{ marginBottom: '2rem' }}
        >
          <img 
            src="/offcourt_logo.png" 
            alt="OffCourt Official League" 
            style={{ 
              width: '120px', 
              height: '120px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))'
            }} 
          />
        </motion.div>

        {/* League Title */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '0.5rem',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            letterSpacing: '0.5px',
            color: '#1C563D'
          }}
        >
          OffCourt Official League
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          style={{ 
            fontSize: '1.25rem', 
            opacity: 0.8,
            marginBottom: '3rem',
            fontWeight: '400',
            letterSpacing: '0.5px',
            color: '#6B7280'
          }}
        >
          Professional Padel League Management Platform
        </motion.p>

        {/* Loading Progress Bar */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: '80%', opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          style={{
            width: '80%',
            maxWidth: '400px',
            height: '6px',
            backgroundColor: 'rgba(28, 86, 61, 0.1)',
            borderRadius: '3px',
            overflow: 'hidden',
            marginBottom: '2rem',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          <motion.div
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #1C563D 0%, #2D7A5A 100%)',
              borderRadius: '3px',
              boxShadow: '0 0 8px rgba(28, 86, 61, 0.3)'
            }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </motion.div>

        {/* Loading Text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          style={{ 
            fontSize: '1rem', 
            opacity: 0.7, 
            marginBottom: '2rem',
            fontWeight: '500',
            color: '#6B7280'
          }}
        >
          Loading your league experience...
        </motion.p>
      </motion.div>

      {/* Powered by ThinkinCode */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        style={{ 
          textAlign: 'center', 
          zIndex: 2,
          position: 'relative',
          marginTop: '2rem'
        }}
      >
        <p style={{ 
          fontSize: '0.875rem', 
          opacity: 0.6, 
          marginBottom: '0.75rem',
          fontWeight: '300',
          color: '#6B7280'
        }}>
          Powered by
        </p>
        <a
          href="https://thinkincode.co.za"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#1C563D',
            textDecoration: 'none',
            fontSize: '1.125rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'rgba(28, 86, 61, 0.05)',
            borderRadius: '2rem',
            border: '1px solid rgba(28, 86, 61, 0.2)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(28, 86, 61, 0.1)';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(28, 86, 61, 0.05)';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
        >
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600' }}>thinkinCode(</span>
          <img 
            src="/thinkincode-emblem.png" 
            alt="ThinkinCode Emblem" 
            style={{ 
              width: '35px', 
              height: '35px',
              objectFit: 'contain'
            }} 
          />
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600' }}>);</span>
        </a>
      </motion.div>
    </motion.div>
  );
};

export default SplashScreen;